/**
 * Affiliate payment periods. The admin picks the closing days of the month (Admin › Pagamentos):
 * a period ends at 00:00 in Brasília of a closing day ("virada do dia") and the next one starts
 * right there. A closing day the month does not have (30 in February, 31 in 30-day months) falls on
 * the 1st of the next month. A calendar month is the closing day [1].
 *
 * Example, days [15, 30]: … | 15/10 00:00 → 30/10 00:00 | 30/10 00:00 → 15/11 00:00 | …
 * Brasília is UTC−3 all year (no daylight saving time since 2019).
 */
const BRT_OFFSET_MS = 3 * 3_600_000;

/** Calendar month, the rule before closing days existed. */
export const DEFAULT_CLOSING_DAYS: readonly number[] = [1];

export const CLOSING_DAY_PRESETS: readonly { key: string; label: string; days: readonly number[] }[] = [
  { key: 'mensal', label: 'Mensal (dia 1º)', days: [1] },
  { key: 'quinzenal', label: 'Quinzenal (dias 15 e 30)', days: [15, 30] },
  { key: 'semanal', label: 'Semanal (dias 1, 8, 15 e 22)', days: [1, 8, 15, 22] },
];

/**
 * A period is closed this long after its cut: operations bought just before 00:00 settle within
 * minutes (Digital lasts up to 15 min), and their commission lines are written as they settle.
 */
export const PAYOUT_CLOSE_DELAY_MS = 3_600_000;

export interface PayoutPeriod {
  /** First instant of the period (a cut). */
  start: Date;
  /** First instant after the period (the next cut, exclusive). */
  end: Date;
}

/** Valid closing days: whole days 1–31, at least one, sorted, without repeats. */
export function normalizeClosingDays(days: readonly number[]): number[] {
  if (!days.length || days.some((d) => !Number.isInteger(d) || d < 1 || d > 31)) throw new Error('Closing days must be whole days from 1 to 31');
  return [...new Set(days)].sort((a, b) => a - b);
}

/** Cut instants (ms) of one Brasília month; a missing day falls on the 1st of the next month. */
function cutsOfMonth(days: readonly number[], y: number, m0: number): number[] {
  const length = new Date(Date.UTC(y, m0 + 1, 0)).getUTCDate();
  return days.map((d) => (d <= length ? Date.UTC(y, m0, d) : Date.UTC(y, m0 + 1, 1)) + BRT_OFFSET_MS);
}

/** Every cut around an instant (two months on each side always holds the cuts before and after it). */
function cutsAround(days: readonly number[], at: Date): number[] {
  const local = new Date(at.getTime() - BRT_OFFSET_MS);
  const y = local.getUTCFullYear();
  const m0 = local.getUTCMonth();
  const all = new Set<number>();
  for (let i = -2; i <= 2; i++) for (const t of cutsOfMonth(days, y, m0 + i)) all.add(t);
  return [...all].sort((a, b) => a - b);
}

/** The period an instant falls in: [last cut ≤ at, first cut > at). */
export function payoutPeriodAt(days: readonly number[], at: Date): PayoutPeriod {
  const cuts = cutsAround(days, at);
  const t = at.getTime();
  const start = cuts.filter((c) => c <= t).at(-1)!;
  const end = cuts.find((c) => c > t)!;
  return { start: new Date(start), end: new Date(end) };
}

/** The next `n` cuts after an instant (when the coming periods close). */
export function nextCuts(days: readonly number[], at: Date, n = 3): Date[] {
  const out: Date[] = [];
  let cursor = at;
  while (out.length < n) {
    const { end } = payoutPeriodAt(days, cursor);
    out.push(end);
    cursor = end;
  }
  return out;
}

/** 'dd/mm' (or 'dd/mm/aaaa') of the Brasília day an instant falls in. */
function brDay(t: number, withYear: boolean): string {
  const iso = new Date(t - BRT_OFFSET_MS).toISOString();
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}${withYear ? `/${iso.slice(0, 4)}` : ''}`;
}

const MONTHS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

/**
 * "15/10 a 29/10" (the last day is the day before the cut); a whole calendar month reads
 * "Outubro de 2026". The year shows only when the period crosses into another year.
 */
/** Whether a period is exactly one Brasília calendar month (the monthly rule, [1]). */
export function isWholeMonth(start: Date | string, end: Date | string): boolean {
  const sl = new Date(new Date(start).getTime() - BRT_OFFSET_MS);
  const el = new Date(new Date(end).getTime() - BRT_OFFSET_MS);
  return (
    sl.getUTCDate() === 1 && sl.getUTCHours() === 0 && el.getUTCDate() === 1 && el.getUTCHours() === 0 &&
    el.getUTCFullYear() * 12 + el.getUTCMonth() - (sl.getUTCFullYear() * 12 + sl.getUTCMonth()) === 1
  );
}

export function payoutPeriodLabel(start: Date | string, end: Date | string): string {
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  const sl = new Date(s - BRT_OFFSET_MS);
  if (isWholeMonth(start, end)) {
    const name = MONTHS[sl.getUTCMonth()]!;
    return `${name.charAt(0).toUpperCase()}${name.slice(1)} de ${sl.getUTCFullYear()}`;
  }
  const crossesYear = sl.getUTCFullYear() !== new Date(e - 1 - BRT_OFFSET_MS).getUTCFullYear();
  return `${brDay(s, crossesYear)} a ${brDay(e - 1, crossesYear)}`;
}

/** "dias 15 e 30" / "dia 1º" — for the admin and the affiliate panel. */
export function closingDaysLabel(days: readonly number[]): string {
  const names = days.map((d) => (d === 1 ? '1º' : String(d)));
  if (names.length === 1) return `dia ${names[0]}`;
  return `dias ${names.slice(0, -1).join(', ')} e ${names.at(-1)}`;
}
